import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import './SideMenu.css';

// Custom Icon Component for SVG icons
const CustomIcon: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    className="sidemenu-icon"
  />
);

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  iconSrc: string;
  badgeCount?: number;
}

const navigationItems: NavigationItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/chart-square.svg' },
  { id: 'applications', label: 'Applications', path: '/applications', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/mail.svg' },
  { id: 'notifications', label: 'Notifications', path: '/notifications', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/notification-text.svg' },
  { id: 'categories', label: 'Categories', path: '/categories', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/tag-2.svg' },
  { id: 'cities', label: 'Cities', path: '/cities', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/category-2.svg' },
  { id: 'vendors', label: 'Vendors', path: '/vendors', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/shop.svg' },
  { id: 'transactions', label: 'Transactions', path: '/transactions', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/moneys.svg' },
  { id: 'promotions', label: 'Promotion', path: '/promotions', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/chart-breakout-square.svg' },
  { id: 'catalogue', label: 'Catalogue', path: '/catalogue', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/category-2.svg'},
  { id: 'discounts', label: 'Discounts', path: '/discounts', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/ticket-discount.svg' },
  { id: 'orders', label: 'Orders', path: '/orders', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/message-alert-square.svg' },
  { id: 'reported-problems', label: 'Reported Problems', path: '/reported-problems', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/message-alert-square.svg' },
  { id: 'marketing', label: 'Marketing', path: '/marketing', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/notification-text.svg' },
  { id: 'event-planner', label: 'Event Planner', path: '/event-planner', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/chart-square.svg' },
];

const bottomNavigationItems: NavigationItem[] = [
  { id: 'admins', label: 'Admins', path: '/admins', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/users.svg' },
  { id: 'otps', label: 'Active OTPs', path: '/otps', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/notification-text.svg' },
  { id: 'logout', label: 'Logout', path: '/logout', iconSrc: 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logout.svg' },
];

interface SideMenuProps {
  children: React.ReactNode;
}

const SideMenu: React.FC<SideMenuProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth(); // Only needed for logout functionality
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    if (path === '/logout') {
      logout();
      navigate('/login');
    } else {
      navigate(path);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const renderNavigationItem = (item: NavigationItem) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    
    return (
      <ListItem key={item.id} disablePadding className="sidemenu-nav-item">
        <ListItemButton
          onClick={() => handleNavigation(item.path)}
          className={`sidemenu-nav-button ${isActive ? 'active' : ''}`}
        >
          <ListItemIcon className="sidemenu-nav-icon">
            <CustomIcon src={item.iconSrc} alt={item.label} />
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            className={`sidemenu-nav-text ${isActive ? 'active' : ''}`}
            primaryTypographyProps={{
              className: `sidemenu-nav-text ${isActive ? 'active' : ''}`,
            }}
          />
          {/* Show badge for items with badgeCount (not Applications) */}
          {item.badgeCount && item.badgeCount > 0 && (
            <Box className="sidemenu-badge">
              {item.badgeCount}
            </Box>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box className="sidemenu-drawer">
      {/* Logo Section */}
      <Box className="sidemenu-logo-section">
        <Box className="sidemenu-logo-wrapper">
          <Box
            component="img"
            src="https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png"
            alt="Eventi"
            className="sidemenu-logo"
          />
        </Box>
      </Box>

      {/* Main Navigation */}
      <Box className="sidemenu-nav-section">
        <List className="sidemenu-nav-list">
          {navigationItems.map(renderNavigationItem)}
        </List>

        {/* Bottom Navigation */}
        <List className="sidemenu-nav-list-bottom">
          {bottomNavigationItems.map(renderNavigationItem)}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box className="sidemenu-container">
      {/* Sidebar */}
      <Box component="nav" className="sidemenu-nav">
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
          }}
          classes={{
            paper: 'sidemenu-drawer-paper',
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
          }}
          classes={{
            paper: 'sidemenu-drawer-paper desktop',
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box component="main" className="sidemenu-main">
        {children}
      </Box>
    </Box>
  );
};

export default SideMenu;

